import React, { Fragment, memo, useEffect, useMemo, useState } from 'react'
import styles from './TeamMembers.module.scss'
import SelectedOptionTopBar from '../../../common/components/selected-option-top-bar/SelectedOptionTopBar'
import List from '../../../common/components/list/List'
import SelectedListItem from '../../../common/components/list/selected-list-item/SelectedListItem'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useLazyGetAllTeamMembersQuery, useRemoveTeamMemberMutation, useToggleActivateTeamMemberMutation } from '../../../service/solar-provider-service'
import { getCurrentSolarProviderCompanyId } from '../../../guards/common/authentification-functions'
import ConfirmationDialog from '../../../common/components/dialog/ConfirmationDialog'
import { getErrorMessage } from '../../../utils/error-handler'
import CommonLoadingSpinner from '../../../common/components/loading-spinner/CommonLoadingSpinnerForPage'

type Props = {}

type TeamMember = {
    id: string,
    firstName: string,
    lastName: string,
    type: string,
    status: string,
    user: {
        id: string;
    }
}

interface ConfirmationContent {
    title: string;
    description: string;
    btnText: string;
    actionLoading: boolean;
    confirmAction: () => Promise<void>;
    itemToRemove?: TeamMember;
}

const TeamMembers: React.FC<Props> = memo((props: Props) => {
    const [ teamMembers, setTeamMembers] = useState<TeamMember[]>();
    const [ selectedItem, setSelectedItem] = useState<TeamMember>();
    const [ debouncedSearch, setDebouncedSearch ] = useState<string>('');
    const [ showConfirmation, setShowConfirmation ] = useState<boolean>(false);
    const [ confirmationContent, setConfirmationContent ] = useState<ConfirmationContent>({
        title: '',
        description: '',
        btnText: '',
        actionLoading: false,
        confirmAction: async () => {}
    });

    const navigate = useNavigate();
    const companyId = useMemo(()=>{
        return getCurrentSolarProviderCompanyId() ?? "";
    },[])

    const [getAllTeamMembers, { 
        data: teamMembersData, 
        error: teamMembersError, 
        isLoading: isLoadingTeamMembers 
      }] = useLazyGetAllTeamMembersQuery();
      
      const [removeTeamMember, { 
        isLoading: isRemovingMember 
      }] = useRemoveTeamMemberMutation();
      
      const [toggleActivateTeamMember, { 
        isLoading: isTogglingMember 
      }] = useToggleActivateTeamMemberMutation();

      useEffect(() => {
        getAllTeamMembers({ 
            companyId,
            search: debouncedSearch
        });
    }, [debouncedSearch, getAllTeamMembers, companyId]);

    useEffect(() => {
        if (teamMembersData) {
            setTeamMembers(teamMembersData);
        }
    }, [teamMembersData]);

    useEffect(() => {
        if (teamMembersError && 'data' in teamMembersError) {
            const errorData = teamMembersError.data as any;
        
        }
    }, [teamMembersError]);

    const selectListItem = (item: any) => {
        setSelectedItem(item)
    }

    const removeMember = async (item: TeamMember) => {
        // Show confirmation dialog instead of immediate removal
        setConfirmationContent({
            title: 'Remove Team Member',
            description: `Are you sure you want to remove ${item.firstName} ${item.lastName} from your team?`,
            btnText: 'Remove',
            actionLoading: isRemovingMember,
            confirmAction: async () => {
                await removeTeamMember({ userId: item.user.id}).unwrap();
            },
            itemToRemove: item
        });
        setShowConfirmation(true);
    }

    const handleConfirmAction = async () => {
        try {
            await confirmationContent.confirmAction();
            setShowConfirmation(false);
        } catch (error: any) {
            toast.error(getErrorMessage(error?.data))
            console.error("Error executing confirmed action:", error);
        }
    };

    const toggleMemberActivity = async () => {
        if (!selectedItem) return;
        
        const newStatus = selectedItem.status === 'Active' ? 'Deactivated' : 'Active';
        const action = selectedItem.status === 'Active' ? 'deactivate' : 'activate';
        
        setConfirmationContent({
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} Team Member`,
            description: `Are you sure you want to ${action} ${selectedItem.firstName} ${selectedItem.lastName}?`,
            btnText: action.charAt(0).toUpperCase() + action.slice(1),
            actionLoading: isTogglingMember,
            confirmAction: async () => {
                try {
                    await toggleActivateTeamMember({
                        userId: selectedItem.user.id
                    }).unwrap();

                    // Update the status locally
                    const updatedMembers = teamMembers?.map(member => {
                        if (member.user.id === selectedItem.user.id) {
                            return {
                                ...member,
                                status: newStatus
                            };
                        }
                        return member;
                    });

                    setTeamMembers(updatedMembers);
                    
                    setSelectedItem({
                        ...selectedItem,
                        status: newStatus
                    });
                    
                    toast.success(`${selectedItem.firstName} ${selectedItem.lastName} has been ${action}d`);
                } catch (error: any) {
                    toast.error(error?.data?.title ?? `There was an error ${action}ing the team member`);
                }
            }
        });
        setShowConfirmation(true);
    }    

    const searchList = (searchContent: string) => {
        setDebouncedSearch(searchContent);
    }

    return (
        <Fragment>
            {showConfirmation && (
                            <ConfirmationDialog
                                actionLoading={confirmationContent.actionLoading}
                                btnText={confirmationContent.btnText}
                                onCloseDialog={() => setShowConfirmation(false)}
                                onConfirm={handleConfirmAction}
                                title={confirmationContent.title}
                                description={confirmationContent.description}
                            />
                        )}
            <SelectedOptionTopBar title='View Team Members' />
            <div className={styles.mainPane}>
                <CommonLoadingSpinner loading={isLoadingTeamMembers}>
                   <List title='Team Members' buttonTitle='Add New Team Member' onSelectListItem={selectListItem} items={teamMembers} onButtonHandler={removeMember} onSearchHandler={searchList} onTopButtonHandler={() => navigate('add')} />
                </CommonLoadingSpinner>
                {selectedItem != null && (
                    <div className={styles.itemDetails}>
                        <SelectedListItem item={selectedItem} onBackHandler={() => setSelectedItem(undefined)} toggleButtonHandler={toggleMemberActivity}/>
                    </div>
                )}
            </div>
        </Fragment>
    )
})

export default TeamMembers